import { Component, Element, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll, nextTick } from '../../utils';
import { getFocusableChildren } from '../../utils/get-focusable-children';
import { ModalPalette } from './types';

export interface AbdsFocusableElement extends HTMLElement {
  setFocus?: () => Promise<void>;
}

enum FocusCycleDirection {
  Forward,
  Backward,
}

/**
 * @slot modal-body - Adds body content.
 * @slot modal-footer - Adds footer content.
 */
@Component({
  shadow: true,
  styleUrl: 'modal.css',
  tag: 'abds-modal',
})
export class Modal {
  //--------------------------------------------------------------------------
  //
  //  Element
  //
  //--------------------------------------------------------------------------

  @Element() host: HTMLAbdsModalElement;

  //--------------------------------------------------------------------------
  //
  //  Props
  //
  //--------------------------------------------------------------------------

  /** Sets header text. */
  @Prop() headerText: string;

  /** Adds an abds-icon on left side of header text. */
  @Prop({ reflect: true }) icon: string;

  /** Sets open/closed state. */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) open = false;

  /** Sets header text color. */
  @Prop({ reflect: true }) palette: ModalPalette = 'basic';

  //--------------------------------------------------------------------------
  //
  //  Prop Watchers
  //
  //--------------------------------------------------------------------------

  @Watch('open')
  handleOpenStateChange(newOpen: boolean, oldOpen?: boolean) {
    if (newOpen === true) {
      disableBodyScroll(this.host);
      this.returnFocusEl = document.activeElement as HTMLElement;
    }
    if (newOpen === false && oldOpen !== undefined) {
      enableBodyScroll(this.host);
    }
  }

  //--------------------------------------------------------------------------
  //
  //  Private Properties
  //
  //--------------------------------------------------------------------------

  private bottomObserver: IntersectionObserver;

  private closeButtonEl: HTMLButtonElement;

  private focusCycleDirection = FocusCycleDirection.Forward; // fwd or back

  private focusGateStartEl: HTMLElement;

  private modalBodyBottomEl: HTMLElement;

  private modalBodyEl: HTMLElement;

  private modalBodyTopEl: HTMLElement;

  private modalBodyWrapperEl: HTMLElement;

  private returnFocusEl: AbdsFocusableElement;

  private topObserver: IntersectionObserver;

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private closeModal = () => {
    this.open = false;
    this.returnFocusEl && this.returnFocus();
  };

  private getSlotByName(slotName: string) {
    return [...this.host.children].filter((slot) => slot.getAttribute('slot') === slotName)[0] || undefined;
  }

  private focusNextFocusableElement = async () => {
    await nextTick();
    if (this.focusCycleDirection === FocusCycleDirection.Forward) {
      this.closeButtonEl?.focus();
    } else {
      const focusableElements = [this.closeButtonEl, ...getFocusableChildren(this.host)];
      focusableElements[focusableElements.length - 1]?.focus();
    }
  };

  private initIntersectionObserver() {
    if (!this.modalBodyEl) this.modalBodyEl = this.getSlotByName('modal-body') as HTMLElement;

    /*
    Using an intersection observer rather than scroll event for performance reasons:
    see https://css-tricks.com/an-explanation-of-how-the-intersection-observer-watches/#aa-performance-intersection-observer-versus-scroll-events

    Threshold can take a number or an array of numbers; it represents the visible percentage of the target element relative to the root.
    The IntersectionObserver reports each time a threshold number is crossed.
    https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#thresholds
    */

    const threshold = [0, 0.5, 1];

    if (this.modalBodyEl) {
      this.bottomObserver = new IntersectionObserver(this.observeBottomIntersection, {
        root: this.modalBodyWrapperEl,
        threshold,
      });
      this.topObserver = new IntersectionObserver(this.observeTopIntersection, {
        root: this.modalBodyWrapperEl,
        threshold,
      });

      this.bottomObserver.observe(this.modalBodyBottomEl);
      this.topObserver.observe(this.modalBodyTopEl);
    }
  }

  private observeBottomIntersection = async (entries: IntersectionObserverEntry[]) => {
    for (const entry of entries) {
      this.isBodyBottomOverflown = !entry.isIntersecting;
    }
  };

  private observeTopIntersection = async (entries: IntersectionObserverEntry[]) => {
    for (const entry of entries) {
      this.isBodyTopOverflown = !entry.isIntersecting;
    }
  };

  private returnFocus = () => {
    if (typeof this.returnFocusEl.setFocus !== 'function' || typeof this.returnFocusEl.focus !== 'function') return;
    this.returnFocusEl.setFocus() || this.returnFocusEl.focus();
  };

  private wrapFocus = () => {
    this.focusGateStartEl.focus();
  };

  //--------------------------------------------------------------------------
  //
  //  Private State
  //
  //--------------------------------------------------------------------------

  /** Determines if footer should be shown. */
  @State() hasFooter: boolean;

  /**
   * Reflects the condition where .modal-body content is taller than
   * .modal-body-wrapper.
   */
  @State() isBodyBottomOverflown: boolean;

  @State() isBodyTopOverflown: boolean;

  //--------------------------------------------------------------------------
  //
  //  Event Listeners
  //
  //--------------------------------------------------------------------------

  @Listen('keyup', { target: 'window' })
  handleKeyup(event: KeyboardEvent): void {
    if (this.open && event.key === 'Escape') {
      this.closeModal();
    }
    if (this.open && event.key === 'Shift') {
      this.focusCycleDirection = FocusCycleDirection.Forward;
    }
  }

  @Listen('keydown', { target: 'window' })
  handleKeydown(event: KeyboardEvent): void {
    if (this.open && event.key === 'Shift') {
      this.focusCycleDirection = FocusCycleDirection.Backward;
    }
  }

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  componentDidLoad() {
    this.handleOpenStateChange(this.open);
    this.initIntersectionObserver();
  }

  componentDidRender() {
    this.focusNextFocusableElement();
  }

  componentWillLoad() {
    if (!this.host.hasAttribute('aria-labelledby') && this.headerText) {
      this.host.setAttribute('aria-label', this.headerText);
    }
    this.hasFooter = this.getSlotByName('modal-footer') !== undefined;
  }

  disconnectedCallback() {
    clearAllBodyScrollLocks();
    this.bottomObserver.disconnect();
    this.topObserver.disconnect();
  }

  render() {
    return (
      <Host
        aria-hidden={this.open ? 'false' : 'true'}
        palette={this.palette}
        role="dialog"
        style={this.open ? { display: 'flex' } : { display: 'none' }}
      >
        <div class="backdrop" />
        <div
          id="focus-gate-start"
          onFocus={this.focusNextFocusableElement}
          ref={(el) => {
            this.focusGateStartEl = el;
          }}
          tabindex="0"
        ></div>
        <div class="modal">
          <div class={{ 'modal-header-wrapper': true, 'text-danger': this.palette === 'danger' }}>
            {this.icon && (
              <abds-icon class="decorative-icon" icon={this.icon} size="2xl" stroke-width="medium"></abds-icon>
            )}
            {this.headerText}
            <button
              aria-label="close modal dialog"
              class="close-button"
              onClick={this.closeModal}
              ref={(el) => {
                this.closeButtonEl = el;
              }}
            >
              <abds-icon icon="remove" size="2xl" stroke-width="semibold" />
            </button>
          </div>
          <div
            class={{
              'modal-body-wrapper': true,
              'mask-bottom': this.isBodyBottomOverflown === true,
              'mask-top': this.isBodyTopOverflown === true,
              'rounded-b': this.hasFooter === false,
            }}
            ref={(el) => (this.modalBodyWrapperEl = el)}
          >
            <div class="modal-body-top-el" ref={(el) => (this.modalBodyTopEl = el)}></div>
            <slot name="modal-body" />
            <div class="modal-body-bottom-el" ref={(el) => (this.modalBodyBottomEl = el)}></div>
          </div>
          {this.hasFooter && (
            <div class="modal-footer-wrapper">
              <slot name="modal-footer" />
            </div>
          )}
        </div>
        <div id="focus-gate-end" onFocus={this.wrapFocus} tabindex="0"></div>
      </Host>
    );
  }
}
